import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Bot, Maximize2, Minimize2, X, Send } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAI } from '../../context/AIContext';
import { aiChatService } from '../../services/aiChatService';

export const AIChatbot = () => {
    const { isAuthenticated } = useAuth();
    const [isMinimized, setIsMinimized] = useState(false);
    const [messages, setMessages] = useState<any[]>([{ id: 1, type: 'bot', content: "Hello! I'm your SBAJ Cybersecurity Assistant. Ask me about phishing, passwords, ransomware, and more!", timestamp: new Date() }]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const { config, isChatOpen, toggleChat } = useAI();

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    const getBotResponse = async (msg: string): Promise<string> => {
        return await aiChatService.getAIResponse(msg, config);
    };

    const send = async () => {
        if (!input.trim()) return;
        const messageText = input; // Capture input immediately
        const userMsg = { id: Date.now(), type: 'user', content: messageText, timestamp: new Date() };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsTyping(true);

        try {
            const response = await getBotResponse(messageText);
            const botMsg = { id: Date.now() + 1, type: 'bot', content: response, timestamp: new Date() };
            setMessages(prev => [...prev, botMsg]);
        } catch (error) {
            console.error("Error getting bot response:", error);
            const errorMsg = { id: Date.now() + 1, type: 'bot', content: "I'm having trouble connecting right now. Please try again later.", timestamp: new Date() };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setIsTyping(false);
        }
    };

    const formatMessage = (content: string) => {
        return content.split('\n').map((line, i) => {
            if (line.startsWith('**') && line.endsWith('**')) {
                return <h4 key={i} className="font-bold text-gray-900 dark:text-white mt-2 mb-1">{line.replace(/\*\*/g, '')}</h4>;
            }
            if (line.startsWith('• ')) return <li key={i} className="ml-4 text-gray-700 dark:text-gray-300 text-sm">{line.substring(2)}</li>;
            if (line.match(/^\d+\./)) return <li key={i} className="ml-4 text-gray-700 dark:text-gray-300 text-sm list-decimal">{line.substring(line.indexOf('.') + 1)}</li>;
            if (line.trim() === '') return <br key={i} />;
            return <p key={i} className="text-gray-700 dark:text-gray-300 text-sm mb-1">{line}</p>;
        });
    };

    // Dragging Logic
    const [position, setPosition] = useState({ x: window.innerWidth - 100, y: window.innerHeight - 100 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                setPosition({
                    x: e.clientX - dragOffset.x,
                    y: e.clientY - dragOffset.y
                });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]); // Added dragOffset to deps

    if (!isAuthenticated) return null;

    return (
        <>
            {!isChatOpen && (
                <button
                    onClick={() => { if (!isDragging) toggleChat(); }}
                    onMouseDown={handleMouseDown}
                    style={{ left: position.x, top: position.y }}
                    className="fixed z-50 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform animate-pulse cursor-move"
                >
                    <MessageCircle className="h-6 w-6" />
                </button>
            )}

            {isChatOpen && (
                <div className={`fixed bottom-6 right-6 z-50 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 transition-all ${isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'}`}>
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-t-2xl flex justify-between items-center cursor-move">
                        <div className="flex items-center gap-3">
                            <div className="bg-white/20 p-2 rounded-full"><Bot className="h-5 w-5" /></div>
                            <div><h3 className="font-bold">Cyber Assistant</h3><p className="text-xs opacity-90">Powered by Guardsman Cyber Intelligence (GCI)</p></div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setIsMinimized(!isMinimized)} className="hover:bg-white/20 p-1 rounded">
                                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
                            </button>
                            <button onClick={toggleChat} className="hover:bg-white/20 p-1 rounded"><X className="h-4 w-4" /></button>
                        </div>
                    </div>
                    {/* ... (rest unchanged) ... */}
                    {!isMinimized && (
                        <>
                            <div className="h-[420px] overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
                                {messages.map(msg => (
                                    <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[80%] p-3 rounded-2xl ${msg.type === 'user' ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' : 'bg-white dark:bg-gray-700 border dark:border-gray-600 text-gray-800 dark:text-gray-100'}`}>
                                            {msg.type === 'bot' ? <div className="text-sm">{formatMessage(msg.content)}</div> : <p className="text-sm">{msg.content}</p>}
                                            <p className={`text-xs mt-1 ${msg.type === 'user' ? 'text-white/70' : 'text-gray-400 dark:text-gray-400'}`}>{msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                        </div>
                                    </div>
                                ))}
                                {isTyping && (
                                    <div className="flex justify-start">
                                        <div className="bg-white dark:bg-gray-700 border dark:border-gray-600 text-gray-800 dark:text-gray-100 p-3 rounded-2xl">
                                            <div className="flex gap-1">
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            <div className="p-4 border-t border-gray-100 dark:border-gray-700">
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && send()}
                                        placeholder="Ask about cybersecurity..."
                                        className="flex-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400 dark:placeholder-gray-400"
                                    />
                                    <button onClick={send} className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors">
                                        <Send className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
};
